import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Token refresh state — prevents multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

function getToken() {
  return localStorage.getItem('access_token') || localStorage.getItem('token');
}

function clearAuthState() {
  ['access_token', 'token', 'refresh_token', 'role', 'user_id', 'user_name',
   'user_email', 'customerId', 'actingAsCustomer', 'user_type'].forEach((key) =>
    localStorage.removeItem(key)
  );
}

// Request interceptor — attach Bearer token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — auto-refresh on 401
api.interceptors.response.use(
  (response) => {
    // Parse string responses from the selections endpoint
    if (response.config.url?.includes('/selections') && typeof response.data === 'string') {
      try {
        response.data = JSON.parse(response.data);
      } catch (e) {
        // leave as-is if parse fails
      }
    }
    return response;
  },
  async (error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
      });
    }

    const originalRequest = error.config;

    // Attempt token refresh on 401, but only once per request
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // No refresh token — clear session and reject
      if (!refreshToken) {
        clearAuthState();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the ongoing refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken });
        const { access_token, refresh_token: newRefreshToken } = response.data;

        localStorage.setItem('access_token', access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthState();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const getHousePlanOptions = async (housePlanId, customerId, location) => {
  const response = await api.get(`/api/house-plans/${housePlanId}/options`, {
    params: { customerId, location },
  });
  return response.data;
};

export const saveSelections = async (selections) => {
  if (!selections.customerId) {
    throw new Error('Customer ID is required for saving selections');
  }
  const response = await api.post('/customers/selections', selections);
  return response.data;
};

export const getCustomerSelections = async (customerId) => {
  if (!customerId) return null;

  const response = await api.get(`/customers/${customerId}/selections`);
  const data = response.data;

  if (data && typeof data === 'object') {
    if (data.selections && data.customer && data.metadata) return data;
    if (data.data) return data.data;
    if (data.result) return data.result;
    if (data.customer_id !== undefined && data.selections) return { selections: data.selections };
  }

  return data;
};

export const getMarkets = async () => {
  const response = await api.get('/data/markets');
  return response.data;
};

export const getCollections = async (market) => {
  const response = await api.get('/data/collections', { params: { market } });
  return response.data;
};

export const getHousePlans = async (market, collection) => {
  const response = await api.get('/data/houseplans', { params: { market, collection } });
  return response.data;
};

export const getSubdivisions = async (market) => {
  const response = await api.get('/data/subdivisions', { params: { market } });
  return response.data;
};

export const getLots = async (subdivision) => {
  const response = await api.get('/data/lots', { params: { subdivision } });
  return response.data;
};


// ========== PHASE 2: ENHANCED API ENDPOINTS ==========

// ===== PRICING ENGINE INTEGRATION =====
export const pricingEngineAPI = {
  // Calculate price with dependencies
  calculatePriceWithDependencies: async (costItemId, selectedOptions, vendorBidPrices) => {
    const response = await api.post('/pricing-engine/calculate-dependencies', {
      costItemId,
      selectedOptions,
      vendorBidPrices,
    });
    return response.data;
  },

  // Get pricing optimization suggestions
  getOptimizationSuggestions: async (customerId, currentSelections) => {
    const response = await api.post('/pricing-engine/optimize', {
      customerId,
      currentSelections,
    });
    return response.data;
  },

  // Get dynamic pricing updates
  getDynamicPricing: async (projectParams) => {
    const response = await api.post('/pricing-engine/dynamic-pricing', projectParams);
    return response.data;
  },
};

// ===== OPERATIONS MANAGEMENT =====
export const operationsAPI = {
  // Project management
  getProjects: async (filters = {}) => {
    const response = await api.get('/operations/projects', { params: filters });
    return response.data;
  },

  getProjectDetails: async (projectId) => {
    const response = await api.get(`/operations/projects/${projectId}`);
    return response.data;
  },

  updateProjectStatus: async (projectId, status, notes) => {
    const response = await api.put(`/operations/projects/${projectId}/status`, {
      status,
      notes,
    });
    return response.data;
  },

  // Vendor management for operations
  getVendorPerformance: async (vendorId) => {
    const response = await api.get(`/operations/vendors/${vendorId}/performance`);
    return response.data;
  },

  approveVendorBid: async (bidId, notes = '') => {
    const response = await api.post(`/operations/bids/${bidId}/approve`, { notes });
    return response.data;
  },

  rejectVendorBid: async (bidId, reason) => {
    const response = await api.post(`/operations/bids/${bidId}/reject`, { reason });
    return response.data;
  },

  // Resource management
  getResourceAllocation: async (projectId) => {
    const response = await api.get(`/operations/projects/${projectId}/resources`);
    return response.data;
  },

  updateResourceAllocation: async (projectId, resources) => {
    const response = await api.put(`/operations/projects/${projectId}/resources`, {
      resources,
    });
    return response.data;
  },
};

// ===== ENHANCED CUSTOMER PORTAL =====
export const customerPortalAPI = {
  // Customer profile management
  getCustomerProfile: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/profile`);
    return response.data;
  },

  updateCustomerProfile: async (customerId, profileData) => {
    const response = await api.put(`/customers/${customerId}/profile`, profileData);
    return response.data;
  },

  // Order history and tracking
  getOrderHistory: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/orders`);
    return response.data;
  },

  getOrderDetails: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  // Communication
  getMessages: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/messages`);
    return response.data;
  },

  sendMessage: async (recipientId, message, messageType = 'general') => {
    const response = await api.post('/messages', {
      recipientId,
      message,
      messageType,
    });
    return response.data;
  },

  // Document management
  uploadDocument: async (customerId, formData) => {
    const response = await api.post(`/customers/${customerId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getDocuments: async (customerId) => {
    const response = await api.get(`/customers/${customerId}/documents`);
    return response.data;
  },
};

// ===== ADVANCED ADMIN APIS =====
export const adminAPI = {
  // System configuration
  getSystemConfig: async () => {
    const response = await api.get('/admin/system/config');
    return response.data;
  },

  updateSystemConfig: async (configData) => {
    const response = await api.put('/admin/system/config', configData);
    return response.data;
  },

  // Advanced reporting
  getAdvancedReports: async (reportType, filters = {}) => {
    const response = await api.get(`/admin/reports/${reportType}`, { params: filters });
    return response.data;
  },

  generateReport: async (reportConfig) => {
    const response = await api.post('/admin/reports/generate', reportConfig);
    return response.data;
  },

  // Bulk operations
  bulkUpdateUsers: async (userUpdates) => {
    const response = await api.post('/admin/users/bulk-update', { userUpdates });
    return response.data;
  },

  bulkImportData: async (dataType, formData) => {
    const response = await api.post(`/admin/import/${dataType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Audit logs
  getAuditLogs: async (filters = {}) => {
    const response = await api.get('/admin/audit-logs', { params: filters });
    return response.data;
  },
};

// ===== SECURITY & MFA APIs =====
export const securityAPI = {
  // MFA Management (for all roles, not just vendors)
  getMFAStatus: async () => {
    const response = await api.get('/security/mfa/status');
    return response.data;
  },

  setupMFA: async () => {
    const response = await api.post('/security/mfa/setup');
    return response.data;
  },

  confirmMFA: async (token) => {
    const response = await api.post('/security/mfa/confirm', { token });
    return response.data;
  },

  verifyMFA: async (token) => {
    const response = await api.post('/security/mfa/verify', { token });
    return response.data;
  },

  disableMFA: async (token) => {
    const response = await api.post('/security/mfa/disable', { token });
    return response.data;
  },

  // Security dashboard
  getSecurityDashboard: async () => {
    const response = await api.get('/security/dashboard');
    return response.data;
  },

  getSecurityAlerts: async (severity) => {
    const response = await api.get('/security/audit/alerts', {
      params: { severity },
    });
    return response.data;
  },

  // Rate limiting info
  getRateLimitStatus: async () => {
    const response = await api.get('/security/rate-limit/status');
    return response.data;
  },
};

// ===== ML & AI APIs (for non-vendor roles) =====
export const aiAPI = {
  // Customer recommendations
  getCustomerRecommendations: async (customerId, customerProfile) => {
    const response = await api.post('/recommendation/customer-recommendations', {
      customerId,
      customerProfile,
    });
    return response.data;
  },

  // Price predictions
  getPricePredictions: async (costItemId, projectParams) => {
    const response = await api.post('/ai/predict-price', {
      costItemId,
      projectParams,
    });
    return response.data;
  },

  // Market trends
  getMarketTrends: async () => {
    const response = await api.get('/ai/market-trends');
    return response.data;
  },

  // Customer insights for sales team
  getCustomerInsights: async (customerId) => {
    const response = await api.post('/ai/customer-insights', {
      customerId,
      analysisType: 'comprehensive',
    });
    return response.data;
  },
};

// ===== DYNAMIC PRICING API =====
export const dynamicPricingAPI = {
  // Get vendor bids for a specific cost item
  getBidsByCostItem: async (costItemId) => {
    try {
      const response = await api.get(`/vendors/bids/by-cost-item/${costItemId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bids for cost item:', error);
      return [];
    }
  },

  // Calculate dynamic prices for multiple options
  calculateOptionPrices: async (optionIds, housePlanId) => {
    try {
      const response = await api.post('/vendors/pricing/calculate-option-prices', {
        optionIds,
        housePlanId
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating option prices:', error);
      return { optionPrices: [] };
    }
  },

  // Get cost item mappings for an option
  getCostItemMappings: async (optionId) => {
    try {
      const response = await api.get(`/data/cost-item-mappings/${optionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cost item mappings:', error);
      return [];
    }
  },

  // Get total customer pricing (used by both Sales and Customer interfaces)
  getCustomerTotalPricing: async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/pricing/total`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer total pricing:', error);
      return null;
    }
  },

  // Get customer selection pricing breakdown (unified view for Sales/Customer)
  getCustomerSelectionPricing: async (customerId, includePricingSource = false) => {
    try {
      const response = await api.get(`/customers/${customerId}/selections/pricing`, {
        params: { includePricingSource }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customer selection pricing:', error);
      return null;
    }
  }
};

// ===== REAL-TIME/WEBSOCKET SUPPORT =====
export const realtimeAPI = {
  // Get real-time data snapshots when WebSocket isn't available
  getBidUpdates: async (filters = {}) => {
    const response = await api.get('/websocket/bid-updates', { params: filters });
    return response.data;
  },

  getProjectUpdates: async (projectId) => {
    const response = await api.get(`/websocket/project-updates/${projectId}`);
    return response.data;
  },

  getNotifications: async () => {
    const response = await api.get('/websocket/notifications');
    return response.data;
  },

  markNotificationRead: async (notificationId) => {
    const response = await api.post(`/websocket/notifications/${notificationId}/read`);
    return response.data;
  },
};

// ===== ENHANCED AUTHENTICATION =====
export const authAPI = {
  // Password management
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/password-reset/request', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/password-reset/confirm', {
      token,
      newPassword,
    });
    return response.data;
  },

  // Session management
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await api.post('/auth/refresh', { refreshToken });
    
    // Update stored tokens
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  },

  // Account security
  getLoginHistory: async () => {
    const response = await api.get('/auth/login-history');
    return response.data;
  },

  revokeAllSessions: async () => {
    const response = await api.post('/auth/revoke-all-sessions');
    return response.data;
  },
};

export default api;